/**
 * Derivation of a car wash's migration state from responses the admin panel
 * already fetches.
 *
 * A vendor sync is a *temporary bridge*: it exists so a car wash can run its old
 * system alongside Way during onboarding, and it is meant to end. Nothing in the
 * backend records that lifecycle — enrolment in the nightly sweep is a side
 * effect of storing credentials and withdrawal a side effect of deleting them
 * (see svc-vendor-sync/src/scheduler/syncScheduler.js) — so the stage is derived
 * here instead of stored. That is deliberate: it needs no schema change, no new
 * endpoint, and cannot drift from the credential rows that actually drive the
 * sweep.
 *
 * Everything below is pure so the state machine can be tested without Angular,
 * HTTP, or a fixture-heavy component harness.
 */

import { MigrationImportHistoryItem } from '../../services/customer-migration-import.service';
import {
  VendorAdapterInfo,
  VendorAgentJob,
  VendorCredentialStatus,
} from '../../services/vendor-sync.service';
import { VendorPlanMapping } from '../../services/vendor-plan-mapping.service';

/**
 * Where a car wash is with one vendor.
 *
 * Ordered roughly by lifecycle, but `broken` and `migrated` are terminal-ish and
 * can be reached from anywhere, so this is not a linear progression.
 */
export type VendorSystemState =
  | 'setup_incomplete'
  | 'needs_attention'
  | 'backfilling'
  | 'bridging'
  | 'ready_to_cut_over'
  | 'broken'
  | 'migrated';

/** Visual weight for a state, mapped to the app's surface/text colour pairs. */
export type VendorSystemTone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

/**
 * One table cell: its text, plus the tone the value itself carries.
 *
 * Named cells rather than a `metrics[]` array because the list is a table with
 * fixed columns. A variable-length array let a row quietly render fewer cells
 * than its header promised, which is exactly the bug a table should make
 * impossible.
 */
export interface VendorSystemCell {
  value: string;
  /** Set when the value itself carries the warning, e.g. an invalid mapping count. */
  tone?: VendorSystemTone;
}

/** One row in the systems list — everything the card renders. */
export interface VendorSystem {
  vendor: string;
  displayName: string;
  adapter: VendorAdapterInfo;
  state: VendorSystemState;
  /** Short status word for the pill, e.g. "Bridging". */
  statusLabel: string;
  tone: VendorSystemTone;
  /** One line naming the consequence, not just the condition. */
  summary: string;
  /** "Sep 1 · 70 new", or "Never" / "Not visible" when there is no settled run. */
  lastSync: VendorSystemCell;
  /** "4 valid", "1 skipping members", or "None mapped". */
  plans: VendorSystemCell;
  /** Footer line: how long this bridge has been running, and whether it is still doing anything. */
  transitionNote: string | null;
  /** True when the primary action should be the cut-over, not "Manage". */
  offerCutOver: boolean;
  /** False once credentials are gone — a migrated system is read-only. */
  isLive: boolean;
  /**
   * True when this vendor has no runs inside the history window but older runs
   * may exist, so "never synced" cannot be claimed. See
   * isVendorHistoryIndeterminate.
   */
  historyUnknown: boolean;
  /**
   * This vendor's runs inside the history window, newest first.
   *
   * The same rows `lastSync` and `transitionNote` are derived from, carried
   * through whole so the roster panel can list them. Bounded by
   * HISTORY_PAGE_SIZE *before* the vendor filter, so this is a window rather
   * than the full record — `historyTruncated` is what says so.
   */
  history: readonly MigrationImportHistoryItem[];
  /**
   * True when the history window came back full, so older runs for this vendor
   * may exist beyond it. Distinct from `historyUnknown`, which means the window
   * held nothing for this vendor at all.
   */
  historyTruncated: boolean;
}

/**
 * How many consecutive quiet syncs mean the bridge has stopped doing anything.
 *
 * Three is enough to rule out a single empty night (a vendor outage, or a day
 * with genuinely no signups) without making an operator wait a fortnight for the
 * prompt. Deliberately not configurable — a knob here would be one more thing to
 * get wrong, and the operator can always cut over before we suggest it.
 */
export const QUIET_SYNCS_BEFORE_CUT_OVER = 3;

/**
 * Row cap the history endpoint enforces, mirrored here so a caller can tell a
 * full page from a truncated one without a second request.
 */
export const HISTORY_PAGE_SIZE = 50;

/** Statuses that mean an import run finished and its counts are meaningful. */
const SETTLED_STATUSES = new Set(['completed', 'failed']);

/**
 * Import history rows belonging to one vendor, newest first.
 *
 * Vendor syncs land in history as `csv`, because svc-vendor-sync hands a
 * generated CSV to the same importer. The file name is what distinguishes them —
 * it is written `<vendor>-<date>.csv` — so the prefix is the only available
 * discriminator. This mirrors ImportDataComponent.getHistorySourceLabel rather
 * than inventing a second rule.
 */
export function vendorHistoryFor(
  vendor: string,
  history: readonly MigrationImportHistoryItem[]
): MigrationImportHistoryItem[] {
  const prefix = `${vendor}-`;
  return history
    .filter(item => (item.source_file_name ?? '').startsWith(prefix))
    .slice()
    .sort((a, b) => toTime(b.created_at) - toTime(a.created_at));
}

/**
 * Consecutive most-recent completed syncs that inserted nobody new.
 *
 * Counts only completed runs: a failed sync inserted nothing because it broke,
 * which says nothing about whether the roster has gone quiet, and treating it as
 * evidence would invite a cut-over while the bridge is actually unhealthy.
 * Stops at the first run that inserted someone, so an old quiet streak behind
 * recent activity does not count.
 */
export function quietSyncStreak(vendorHistory: readonly MigrationImportHistoryItem[]): number {
  let streak = 0;
  for (const item of vendorHistory) {
    if (item.status !== 'completed') {
      // An unfinished run tells us nothing yet; a failed one is handled as
      // `broken` elsewhere. Either way the streak cannot be extended past it.
      break;
    }
    if ((item.inserted_count ?? 0) > 0) {
      break;
    }
    streak += 1;
  }
  return streak;
}

/** Largest row limit svc-vendor-sync accepts for a canary sync; mirrors syncController. */
export const MAX_SYNC_LIMIT = 100_000;

/**
 * Read the optional "Row limit" input for Sync now.
 *
 * Blank means a full sync. Anything else must be a whole number in
 * 1..MAX_SYNC_LIMIT — the server refuses the rest, and saying so before the
 * click is kinder than a 400 after it.
 *
 * `badInput` is the number input's own "this is not a number" flag. The browser
 * reports such text (e.g. "1e") as an empty value, so without it a mistyped
 * canary would read as blank — a full sync.
 */
export function parseSyncLimit(
  raw: unknown,
  badInput = false
): { valid: boolean; limit: number | null } {
  if (badInput) return { valid: false, limit: null };
  if (raw === null || raw === undefined || raw === '') return { valid: true, limit: null };
  const limit = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  if (typeof limit !== 'number' || !Number.isInteger(limit)) return { valid: false, limit: null };
  if (limit < 1 || limit > MAX_SYNC_LIMIT) return { valid: false, limit: null };
  return { valid: true, limit };
}

/**
 * Whether the nightly sweep includes this shop. Only an explicit `false` is off:
 * a status without the field (a shop that never set it, or an older server)
 * keeps syncing nightly, as the server does.
 */
export function isNightlySyncOn(
  status: Pick<VendorCredentialStatus, 'nightlySync'> | null
): boolean {
  return status?.nightlySync !== false;
}

/**
 * Whether the agent job on screen was started by the nightly sweep rather than
 * by Sync now. The sweep runs a full, unlimited sync, so an operator who opens
 * the panel during the 05:30 PT sweep needs to see that before reading it as their own run.
 */
export function isScheduledSync(job: Pick<VendorAgentJob, 'options'> | null): boolean {
  return job?.options?.trigger === 'schedule';
}

/** Plan mappings that will not resolve to a live package, so their members import unentitled. */
export function invalidMappingCount(planMapping: VendorPlanMapping | null): number {
  return (planMapping?.plan_package_map ?? []).filter(entry => !entry.valid).length;
}

export function mappedPlanCount(planMapping: VendorPlanMapping | null): number {
  return (planMapping?.plan_package_map ?? []).length;
}

/**
 * Whether the bridge looks finished: it has gone quiet and every mapped plan
 * still resolves.
 *
 * Requires a mapping to exist — a wash with no plans mapped has not finished
 * migrating, it has not started, and suggesting a cut-over there would strand
 * every member without an entitlement.
 */
export function isReadyToCutOver(
  vendorHistory: readonly MigrationImportHistoryItem[],
  planMapping: VendorPlanMapping | null
): boolean {
  if (mappedPlanCount(planMapping) === 0) return false;
  if (invalidMappingCount(planMapping) > 0) return false;
  return quietSyncStreak(vendorHistory) >= QUIET_SYNCS_BEFORE_CUT_OVER;
}

/**
 * Systems this car wash actually uses.
 *
 * Credentials present *or* history present: the first covers a live bridge, the
 * second keeps a cut-over system visible after its credentials are deleted. An
 * adapter the wash never touched has neither, so it is offered in the picker
 * instead of occupying a row that says nothing.
 */
export function listedVendors(
  adapters: readonly VendorAdapterInfo[],
  statuses: Readonly<Record<string, VendorCredentialStatus | null>>,
  history: readonly MigrationImportHistoryItem[]
): VendorAdapterInfo[] {
  return adapters.filter(
    adapter =>
      statuses[adapter.name]?.configured === true ||
      vendorHistoryFor(adapter.name, history).length > 0
  );
}

/** Adapters not yet used by this car wash — the contents of the Add picker. */
export function addableVendors(
  adapters: readonly VendorAdapterInfo[],
  listed: readonly VendorAdapterInfo[]
): VendorAdapterInfo[] {
  const taken = new Set(listed.map(a => a.name));
  return adapters.filter(adapter => !taken.has(adapter.name));
}

/**
 * Whether this vendor's absence from the history window proves anything.
 *
 * The window is capped at HISTORY_PAGE_SIZE rows and mixes every source, since
 * the endpoint takes no vendor filter. A car wash with many file imports can
 * therefore push its vendor runs out of the window entirely — at which point an
 * absence of rows means "we cannot see them", not "they never happened".
 * Reporting that as "Never" would call a nightly bridge dead.
 *
 * Only meaningful when the window was full: a short response has nothing hidden
 * behind it, so an absence there is real.
 */
export function isVendorHistoryIndeterminate(
  vendorHistory: readonly MigrationImportHistoryItem[],
  historyTruncated: boolean
): boolean {
  return vendorHistory.length === 0 && historyTruncated;
}

export interface DeriveVendorSystemInput {
  adapter: VendorAdapterInfo;
  credentialStatus: VendorCredentialStatus | null;
  planMapping: VendorPlanMapping | null;
  history: readonly MigrationImportHistoryItem[];
  /** BullMQ state of a sync job for this vendor, when one is in flight. */
  activeJobState?: string | null;
  /**
   * True when the history response hit its row cap, so the oldest row is not
   * necessarily the first sync.
   *
   * The history endpoint clamps to 50 rows (customerMigrationImportAuditModel
   * .listByCompanyId), and a nightly bridge produces one row a day — so a wash
   * migrating for longer than that would otherwise be told it started on
   * whatever the 50th-newest run happens to be. When truncated we state a floor
   * instead of a date, because a confidently wrong start date is worse than a
   * vaguer true one.
   */
  historyTruncated?: boolean;
}

/**
 * Collapse the four responses into one card.
 *
 * Order matters: the checks run worst-news-first so a broken bridge is never
 * reported as healthy just because it also has unmapped plans. `migrated` is
 * tested before anything credential-shaped because a cut-over system has no
 * credentials to reason about.
 */
export function deriveVendorSystem(input: DeriveVendorSystemInput): VendorSystem {
  const { adapter, credentialStatus, planMapping, activeJobState } = input;
  const displayName = adapter.displayName || adapter.name;
  const vendorHistory = vendorHistoryFor(adapter.name, input.history);
  const lastSettled = vendorHistory.find(item => SETTLED_STATUSES.has(item.status));
  const invalid = invalidMappingCount(planMapping);
  const mapped = mappedPlanCount(planMapping);
  const historyUnknown = isVendorHistoryIndeterminate(
    vendorHistory,
    input.historyTruncated === true
  );

  const base = {
    vendor: adapter.name,
    displayName,
    adapter,
    lastSync: buildLastSyncCell(lastSettled, historyUnknown),
    plans: buildPlansCell(mapped, invalid),
    transitionNote: buildTransitionNote(vendorHistory, input.historyTruncated === true),
    historyUnknown,
    history: vendorHistory,
    historyTruncated: input.historyTruncated === true,
  };

  // Cut over: no credentials, but this wash has pulled from the vendor before.
  if (!credentialStatus?.configured) {
    if (vendorHistory.length > 0) {
      return {
        ...base,
        state: 'migrated',
        statusLabel: 'Migrated',
        tone: 'neutral',
        summary: lastSettled
          ? `Cut over. Last sync ${formatDay(lastSettled.created_at)} — ${formatCount(
              lastSettled.processed_count
            )} customers kept.`
          : 'Cut over. Imported customers and memberships are unaffected.',
        offerCutOver: false,
        isLive: false,
      };
    }
    // Not listed at all in this state — defensive, so a caller that renders an
    // unlisted adapter gets something truthful rather than a false "bridging".
    return {
      ...base,
      state: 'setup_incomplete',
      statusLabel: 'Not connected',
      tone: 'neutral',
      summary: `${displayName} is not connected for this car wash.`,
      offerCutOver: false,
      isLive: false,
    };
  }

  // Environment or store problems make every other signal untrustworthy.
  if (credentialStatus.encryptionConfigured === false) {
    return {
      ...base,
      state: 'broken',
      statusLabel: 'Broken',
      tone: 'danger',
      summary:
        'Credential encryption is not configured on this environment, so this bridge cannot sync or be edited.',
      offerCutOver: false,
      isLive: true,
    };
  }
  if (credentialStatus.unavailableReason) {
    return {
      ...base,
      state: 'broken',
      statusLabel: 'Broken',
      tone: 'danger',
      summary: `Credential storage is unavailable: ${credentialStatus.unavailableReason}`,
      offerCutOver: false,
      isLive: true,
    };
  }

  if (isRunning(activeJobState)) {
    return {
      ...base,
      state: 'backfilling',
      statusLabel: 'Backfilling',
      tone: 'info',
      summary: `Pulling the roster from ${displayName} now.`,
      offerCutOver: false,
      isLive: true,
    };
  }

  // A failed last run means the roster is going stale, whatever else is true.
  if (lastSettled?.status === 'failed') {
    return {
      ...base,
      state: 'broken',
      statusLabel: 'Broken',
      tone: 'danger',
      summary: `Last sync failed${
        lastSettled.error_message ? ` — ${lastSettled.error_message}` : ''
      }. The roster is not being kept current.`,
      offerCutOver: false,
      isLive: true,
    };
  }

  if (credentialStatus.enabled === false) {
    return {
      ...base,
      state: 'needs_attention',
      statusLabel: 'Paused',
      tone: 'warn',
      summary:
        'Not syncing, and vendor tags are hidden. Already-imported customers and their memberships are unaffected.',
      offerCutOver: false,
      isLive: true,
    };
  }

  if (mapped === 0) {
    return {
      ...base,
      state: 'setup_incomplete',
      statusLabel: 'Setup incomplete',
      tone: 'warn',
      summary:
        'Connected, but no plans are mapped — every member would import with nothing to redeem at the gate.',
      offerCutOver: false,
      isLive: true,
    };
  }

  if (invalid > 0) {
    return {
      ...base,
      state: 'needs_attention',
      statusLabel: 'Needs attention',
      tone: 'warn',
      summary: `${invalid} of ${mapped} plan mapping${
        mapped === 1 ? '' : 's'
      } no longer resolves to a live package — those members import with no entitlement.`,
      offerCutOver: false,
      isLive: true,
    };
  }

  // Ranked below the setup and mapping states, which a canary exists to catch —
  // an unmapped plan imports members unentitled, and that must not hide behind
  // this. Above Ready/Bridging, so a switch left off after the canary still
  // replaces the healthy-looking states that would otherwise mask it.
  if (!isNightlySyncOn(credentialStatus)) {
    return {
      ...base,
      state: 'needs_attention',
      statusLabel: 'Manual only',
      tone: 'warn',
      summary:
        'Nightly sync is off — the roster only updates when someone clicks Sync now. Turn it on once the canary passes.',
      offerCutOver: false,
      isLive: true,
    };
  }

  if (isReadyToCutOver(vendorHistory, planMapping)) {
    const streak = quietSyncStreak(vendorHistory);
    return {
      ...base,
      state: 'ready_to_cut_over',
      statusLabel: 'Ready to cut over',
      tone: 'ok',
      summary: `The last ${streak} syncs added nobody new and every plan resolves. This bridge has stopped doing anything.`,
      offerCutOver: true,
      isLive: true,
    };
  }

  return {
    ...base,
    state: 'bridging',
    statusLabel: 'Bridging',
    tone: 'ok',
    summary: `Pulled nightly at 05:30 PT while this car wash runs ${displayName} alongside Way.`,
    offerCutOver: false,
    isLive: true,
  };
}

/**
 * The sync cell. Date and volume are one value, not two columns: a recent sync
 * that added nothing is a finished bridge, and splitting them made that pairing
 * easy to miss.
 *
 * "Not visible" stays distinct from "Never". History is capped, so an absent run
 * is unknown rather than proof the bridge never pulled — reporting 0 would read
 * as broken.
 */
function buildLastSyncCell(
  lastSettled: MigrationImportHistoryItem | undefined,
  historyUnknown: boolean
): VendorSystemCell {
  if (lastSettled) {
    return {
      value: `${formatDay(lastSettled.created_at)} · ${formatCount(lastSettled.inserted_count)} new`,
    };
  }
  return historyUnknown ? { value: 'Not visible', tone: 'neutral' } : { value: 'Never' };
}

/**
 * The plans cell. Names the consequence rather than a bare count: an unmapped
 * plan still imports its members, they just arrive with nothing to redeem at the
 * gate, which is why a warn tone matters more than the number.
 */
function buildPlansCell(mapped: number, invalid: number): VendorSystemCell {
  if (mapped === 0) return { value: 'None mapped', tone: 'warn' };
  if (invalid > 0) return { value: `${invalid} skipping members`, tone: 'warn' };
  return { value: `${mapped} valid`, tone: 'ok' };
}

/**
 * "Bridging since 15 Jul · 34 days · last 3 syncs added no new customers".
 *
 * Derived from the oldest vendor history row, which is the closest thing to a
 * migration start date that exists. Null before the first sync, when there is
 * genuinely nothing to say.
 */
function buildTransitionNote(
  vendorHistory: readonly MigrationImportHistoryItem[],
  truncated: boolean
): string | null {
  if (vendorHistory.length === 0) return null;
  const first = vendorHistory[vendorHistory.length - 1];
  const started = toTime(first.created_at);
  if (!started) return null;

  const days = Math.max(0, Math.floor((Date.now() - started) / 86_400_000));
  const parts = truncated
    ? [`Bridging for ${days}+ days`]
    : [`Bridging since ${formatDay(first.created_at)}`, `${days} day${days === 1 ? '' : 's'}`];
  const streak = quietSyncStreak(vendorHistory);
  if (streak > 0) {
    parts.push(`last ${streak} sync${streak === 1 ? '' : 's'} added no new customers`);
  }
  return parts.join(' · ');
}

function isRunning(state?: string | null): boolean {
  return Boolean(state) && state !== 'completed' && state !== 'failed';
}

function toTime(value?: string | null): number {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function formatDay(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatCount(value?: number | null): string {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

/** The badge at the top of the Customer migration tab: what this environment does with PII. */
export interface PiiMaskingBadge {
  tone: 'ok' | 'warn';
  label: string;
  detail: string;
}

const ENV_LABELS: Record<string, string> = {
  production: 'Production',
  staging: 'Staging',
  qa: 'QA',
  dev: 'Dev',
  local: 'Local',
};

/**
 * CWS-1837: svc-admin-panel masks customer email and phone on save in every
 * environment but production, decided by its own NODE_ENV. The tab can only
 * report that, so the badge names the environment and says which way it goes
 * rather than offering a switch.
 */
export function piiMaskingBadge(env: {
  envName?: string | null;
  bulkImport?: { piiMaskedOnSave?: boolean } | null;
}): PiiMaskingBadge {
  const raw = String(env.envName || '').trim();
  const where =
    ENV_LABELS[raw.toLowerCase()] || (raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'here');
  if (env.bulkImport?.piiMaskedOnSave ?? true) {
    return {
      tone: 'ok',
      label: 'PII masking on',
      detail: `Customer email and phone are masked on save in ${where}.`,
    };
  }
  return {
    tone: 'warn',
    label: 'PII masking off',
    detail: `Customer email and phone are stored as entered in ${where}.`,
  };
}
